import { ErrandResponse } from '@backend/schemas/src/errands'
import { useParams, useNavigate, createFileRoute } from '@tanstack/react-router'
import { Check, Pencil, X } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useDocumentUploadStatus } from '@/api/hooks/useDocumentUploadStatus'
import { useErrandActions } from '@/api/hooks/useErrandActions'
import { useErrandNotesRealtime } from '@/api/hooks/useErrandNotesRealtime'
import { useErrandsRefetchOnUploadSettled } from '@/api/hooks/useErrandsRefetchOnUploadSettled'
import { useThread } from '@/api/hooks/useThread'
import { useErrands } from '@/api/hooks/useErrands'
import { useUpdateErrand } from '@/api/hooks/useUpdateErrand'
import { StateSwitcher } from '@/components/atoms/state-switcher'
import { EmptyState } from '@/components/molecules/empty-state'
import { ErrandServicesSection } from '@/components/organisms/errand-services-section/errand-services-section'
import { ErrandEventActions } from '@/components/organisms/errand-timeline/errand-event-actions'
import { ErrandTimeline } from '@/components/organisms/errand-timeline/errand-timeline'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { usePreviewDocumentFiles } from '@/hooks/useDocumentPreview'
import { useErrandChatBinding } from '@/hooks/useErrandChatBinding'
import { useIndividuals } from '@/hooks/useIndividuals'
import { ReactionMessage } from '@/utils/errand-timeline.utils'
import { useAppDispatch } from '@/redux/hooks'
import { AssistantActions } from '@/redux/reducers/assistant'

const ErrandPage = () => {
  const params = useParams({ from: '/_authenticated/errands/$id' })
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { individualQuery, selectedIndividualId } = useIndividuals()
  const updateErrand = useUpdateErrand()
  const { t } = useTranslation()
  const { data: errands = [] } = useErrands(selectedIndividualId)
  const previewDocumentFiles = usePreviewDocumentFiles()

  const errand = useMemo(
    () => errands.find(errand => errand.id === params.id),
    [errands, params.id],
  )

  const threadId =
    errand && (typeof errand.thread === 'string' ? errand.thread : errand.thread.id)

  const { data: actions = [] } = useErrandActions(errand?.id, selectedIndividualId)
  // The timeline groups documents by the reaction that covered them, and anchors rows to the
  // message that reacted — both of which live on the thread.
  const { data: thread } = useThread(threadId, selectedIndividualId)
  const reactionMessages = useMemo(
    () => (thread?.messages ?? []) as unknown as ReactionMessage[],
    [thread?.messages],
  )

  // Live pipeline status for the errand's unfinished documents, so the timeline shows a file
  // moving through upload → parsing → analysis instead of sitting there looking finished.
  // Recomputed by identity on the id list, not the document objects, so a refetch that changes
  // nothing relevant doesn't tear down the RTDB subscriptions.
  const unfinishedDocumentIds = useMemo(
    () => (errand?.documents ?? []).filter(d => d.status !== 'done').map(d => d.id),
    [errand?.documents],
  )
  const uploadStatuses = useDocumentUploadStatus(
    selectedIndividualId ?? '',
    unfinishedDocumentIds,
  )

  useErrandsRefetchOnUploadSettled(uploadStatuses)

  // A note attached to this errand finishing its background generation changes the title the
  // timeline renders — pull the errand back in when the backend says so.
  useErrandNotesRealtime({ errandId: errand?.id, individualId: selectedIndividualId })

  useErrandChatBinding({ errandId: errand?.id, threadId })

  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [titleDraft, setTitleDraft] = useState('')

  const startEditTitle = () => {
    setTitleDraft(errand?.title ?? '')
    setIsEditingTitle(true)
  }

  const saveTitle = () => {
    const next = titleDraft.trim()
    setIsEditingTitle(false)
    if (!errand || !selectedIndividualId || !next || next === errand.title) return
    updateErrand.mutate({
      errandId: errand.id,
      individualId: selectedIndividualId,
      requestBody: { title: next },
    })
  }

  const onChangeStatus = (status: ErrandResponse['status']) => {
    if (!errand || !selectedIndividualId) return
    updateErrand.mutate(
      {
        errandId: errand.id,
        individualId: selectedIndividualId,
        requestBody: { status },
      },
      {
        // Chat shows a loading bubble until the backend's automated reaction lands — see
        // `pendingErrandReactionId` and `useErrandReactionRealtime`.
        onSuccess: () => dispatch(AssistantActions.setPendingErrandReaction(errand.id)),
      },
    )
  }

  // Open the chat and scroll to a specific message — the one that produced a draft, or the
  // assistant's reaction to an upload or status change.
  const onViewInChat = (messageId: string) => {
    if (!threadId) return
    dispatch(AssistantActions.toggleSideChat(true))
    dispatch(AssistantActions.setActiveThreadId(threadId))
    dispatch(AssistantActions.setActiveMessageId(messageId))
  }

  const onPreviewDocument = (documentId: string) => {
    const doc = errand?.documents.find(d => d.id === documentId)
    if (!doc) return
    previewDocumentFiles(doc, doc.combinedFilePath)
  }

  const onUpload = () => {
    if (!errand) return
    // Errand-scoped route rather than /documents/upload?errandId=, so the sidebar and breadcrumb
    // stay on Ärenden. Same page either way — see document-upload-page.tsx.
    navigate({ params: { id: errand.id }, to: '/errands/$id/upload' })
  }

  const onAddNote = () => {
    if (!errand) return
    // Errand-scoped route rather than /notes/new, so the note attaches to this errand — same
    // reasoning as onUpload above. See create-note-page.tsx.
    navigate({ params: { id: errand.id }, to: '/errands/$id/notes/new' })
  }

  // Errand-scoped too, so reading a note doesn't move the user over to Anteckningar mid-errand —
  // and "back" lands them here rather than in the notes list. See note-detail-page.tsx.
  const onOpenNote = (noteId: string) => {
    if (!errand) return
    navigate({ params: { id: errand.id, noteId }, to: '/errands/$id/notes/$noteId' })
  }

  return (
    <div className="@container space-y-8">
      <StateSwitcher
        emptyState={
          <EmptyState
            body={t('errandsPage.emptyState.description')}
            ctaType="create-content"
            heading={t('errandsPage.emptyState.title')}
          />
        }
        isEmpty={errands.length === 0}
        isLoading={individualQuery.isLoading}
      >
        {errand && (
          <>
            <div className="card">
              {isEditingTitle ? (
                <form
                  className="flex items-center gap-2"
                  onSubmit={e => {
                    e.preventDefault()
                    saveTitle()
                  }}
                >
                  <Input
                    autoFocus
                    onChange={e => setTitleDraft(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Escape') setIsEditingTitle(false)
                    }}
                    value={titleDraft}
                  />
                  <Button aria-label={t('errandsPage.detail.saveTitle')} size="icon" type="submit" variant="ghost">
                    <Check />
                  </Button>
                  <Button
                    aria-label={t('errandsPage.detail.cancelEditTitle')}
                    onClick={() => setIsEditingTitle(false)}
                    size="icon"
                    type="button"
                    variant="ghost"
                  >
                    <X />
                  </Button>
                </form>
              ) : (
                <div className="flex items-center gap-2">
                  <h4 className="text-2xl font-heading font-black">{errand.title}</h4>
                  <Button
                    aria-label={t('errandsPage.detail.editTitle')}
                    onClick={startEditTitle}
                    size="icon"
                    variant="ghost"
                  >
                    <Pencil size={16} />
                  </Button>
                </div>
              )}

              <ErrandServicesSection errand={errand} />

              <div>
                <h4 className="mb-3">{t('errandsPage.info.manageLabel')}</h4>
                <ErrandEventActions
                  errand={errand}
                  onAddNote={onAddNote}
                  onChangeStatus={onChangeStatus}
                  onUpload={onUpload}
                />
              </div>
            </div>

            <div className="space-y-3">
              <h4>{t('errandsPage.timeline.title')}</h4>
              <div className="card">
                <ErrandTimeline
                  actions={actions}
                  errand={errand}
                  messages={reactionMessages}
                  onOpenNote={onOpenNote}
                  onPreviewDocument={onPreviewDocument}
                  onViewInChat={onViewInChat}
                  uploadStatuses={uploadStatuses}
                />
              </div>
            </div>
          </>
        )}
      </StateSwitcher>
    </div>
  )
}

export const Route = createFileRoute('/_authenticated/errands/$id')({
  component: ErrandPage,
})
